import { isNumber } from 'lodash';

import { Duration } from '@/lib/duration';
import { divide } from './numbers.util';

export const DEFAULT_CURRENCY = 'USD' as const;

export type FormatNumberOptions = {
  fallback?: string;
  /** Fixed decimal places when the value is non-integer and compact is false. */
  decimals?: number;
  /** Compact to K/M/B when |value| >= 1000. Default true. */
  compact?: boolean;
  /** Adaptive decimal formatting for |value| < 1 (chart compact mode). */
  adaptiveBelowOne?: boolean;
  /** Round non-integers below compact threshold to whole numbers. */
  roundBelowCompact?: boolean;
  /** When set with decimals, omit fractional digits for integer values. */
  hideDecimalsForIntegers?: boolean;
  /** String prepended to the formatted value (sign stays before prepend for negatives). */
  prepend?: string;
};

export type FormatPercentageOptions = {
  inputScale: 'decimal' | 'percent';
  decimals?: number | null;
  showSymbol?: boolean;
  signed?: boolean;
  fallback?: string;
};

export type DurationStyle = 'human' | 'numeric';

export type FormatDurationOptions =
  | { inputUnit: 'seconds'; style?: DurationStyle; decimals?: number; fallback?: string }
  | { inputUnit: 'hours'; style?: 'human'; fallback?: string };

export type CurrencyStyle = 'raw' | 'localized';

export type FormatCurrencyOptions = {
  style?: CurrencyStyle;
  currency?: string;
  decimals?: number | null;
  fallback?: string;
};

export type FormatScoreOptions = {
  decimals?: number;
  outOf?: number;
  fallback?: string;
};

export type FormatDecimalOptions = {
  decimals?: number | null;
  suffix?: string;
  fallback?: string;
};

export const FORMAT_PRESETS = {
  kpi: { decimals: 1 },
  chartLabel: {},
  chartCompact: { compact: true, decimals: 2, fallback: '0', hideDecimalsForIntegers: true },
  chartMetric: { compact: true, decimals: 2, fallback: '0', hideDecimalsForIntegers: true },
  chartMetricCurrency: {
    compact: true,
    decimals: 2,
    fallback: '0',
    hideDecimalsForIntegers: true,
    prepend: '$',
  },
  plain: { decimals: 2, compact: false },
  axis: { compact: true, roundBelowCompact: true },
  detailed: { style: 'localized' as CurrencyStyle, decimals: 2 },
} as const satisfies Record<string, FormatNumberOptions | FormatCurrencyOptions>;

function formatCompactUnit(
  value: number,
  unitDivisor: number,
  unit: 'K' | 'M' | 'B',
  decimals = 1,
  hideDecimalsForIntegers = false,
): string {
  const scaled = divide(value, unitDivisor);
  const effectiveDecimals = hideDecimalsForIntegers && Number.isInteger(scaled) ? 0 : decimals;
  if (effectiveDecimals > 1) {
    return `${scaled.toFixed(effectiveDecimals)}${unit}`;
  }
  return `${parseFloat(scaled.toFixed(effectiveDecimals))}${unit}`;
}

function formatAdaptiveMetric(value: number, type: 'percentage' | 'float'): string {
  if (value === 0) return '0';

  if (type === 'percentage') {
    if (value < 5) return value.toFixed(2);
    if (value < 10) return value.toFixed(1);
    return value.toFixed(0);
  }

  if (value < 1) return value.toFixed(2);
  if (value < 10) return value.toFixed(1);
  return Math.round(value).toString();
}

function resolveFallback(value: number | null | undefined, fallback: string): string | null {
  if (value == null || Number.isNaN(value)) return fallback;
  return null;
}

function formatRawCurrencyValue(value: number, withSign: boolean): string {
  const rawValue = String(value);
  const [mantissa, exponent] = rawValue.split('e');
  const formattedValue = rawValue.includes('e')
    ? `${Number(Number(mantissa).toFixed(6))}e${exponent}`
    : Number(value.toFixed(value !== 0 && Math.abs(value) < 0.01 ? 6 : 2)).toString();
  if (!withSign) return formattedValue;
  return formattedValue.startsWith('-') ? `-$${formattedValue.slice(1)}` : `$${formattedValue}`;
}

function formatDurationHumanSeconds(seconds: number): string {
  const duration = Duration.from({ seconds });
  const minutes = Math.round(duration.toMinutes());
  const hours = Math.round(duration.toHours());

  if (hours >= 2) return `${formatPlainNumber(hours)} hrs`;
  if (minutes >= 2) return `${formatPlainNumber(minutes)} min`;
  return `${formatPlainNumber(Math.round(duration.toSeconds()))} sec`;
}

function formatDurationHumanHours(hours: number): string {
  if (hours < 1) return `${formatPlainNumber(Math.round(Duration.from({ hours }).toMinutes()))} min`;
  return `${formatPlainNumber(Math.round(hours))} hrs`;
}

function formatCompactMagnitude(
  absValue: number,
  sign: string,
  decimals?: number,
  hideDecimalsForIntegers = false,
): string {
  const compactDecimals = decimals ?? 1;

  if (absValue >= 1_000_000_000) {
    return `${sign}${formatCompactUnit(absValue, 1_000_000_000, 'B', compactDecimals, hideDecimalsForIntegers)}`;
  }
  if (absValue >= 1_000_000) {
    return `${sign}${formatCompactUnit(absValue, 1_000_000, 'M', compactDecimals, hideDecimalsForIntegers)}`;
  }
  if (absValue >= 1000) {
    return `${sign}${formatCompactUnit(absValue, 1000, 'K', compactDecimals, hideDecimalsForIntegers)}`;
  }
  return '';
}

/** en-US grouped number for non-compact display. */
function formatPlainNumber(value: number, decimals?: number): string {
  if (decimals !== undefined && !Number.isInteger(value)) {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: decimals,
    }).format(value);
  }

  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

function applyPrepend(formatted: string, prepend?: string): string {
  if (!prepend) return formatted;
  if (formatted.startsWith('-')) return `-${prepend}${formatted.slice(1)}`;
  return `${prepend}${formatted}`;
}

/** Compact counts — auto K/M/B when |value| >= 1000. */
export function formatNumber(value: number | null | undefined, options?: FormatNumberOptions): string {
  const {
    fallback = '-',
    decimals,
    compact = true,
    adaptiveBelowOne = false,
    roundBelowCompact = false,
    hideDecimalsForIntegers = false,
    prepend,
  } = options ?? {};

  if (!isNumber(value) || Number.isNaN(value)) return fallback;

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  let formatted: string;

  if (compact) {
    const compactLabel = formatCompactMagnitude(absValue, sign, decimals, hideDecimalsForIntegers);
    if (compactLabel) {
      formatted = compactLabel;
      return applyPrepend(formatted, prepend);
    }
  }

  if (adaptiveBelowOne && absValue < 1) {
    formatted = `${sign}${formatAdaptiveMetric(absValue, 'float')}`;
    return applyPrepend(formatted, prepend);
  }

  if (!compact) {
    const num = sign === '-' ? -absValue : absValue;
    formatted = formatPlainNumber(num, decimals);
    return applyPrepend(formatted, prepend);
  }

  if (decimals !== undefined) {
    if (hideDecimalsForIntegers && Number.isInteger(absValue)) {
      formatted = `${sign}${absValue.toString()}`;
    } else {
      formatted = `${sign}${absValue.toFixed(decimals)}`;
    }
    return applyPrepend(formatted, prepend);
  }

  if (roundBelowCompact) {
    formatted = Number.isInteger(absValue) ? `${sign}${absValue}` : `${sign}${absValue.toFixed(0)}`;
    return applyPrepend(formatted, prepend);
  }

  formatted = value.toString();
  return applyPrepend(formatted, prepend);
}

/** Format a decimal value with optional adaptive precision and suffix. */
export function formatDecimal(value: number | null | undefined, options?: FormatDecimalOptions): string {
  const { decimals = null, suffix = '', fallback = 'NA' } = options ?? {};

  if (!isNumber(value)) return fallback;

  const formatted = decimals !== null ? value.toFixed(decimals) : formatAdaptiveMetric(value, 'float');
  return `${formatted}${suffix}`;
}

/** Format a percentage for display. */
export function formatPercentage(value: number | null | undefined, options: FormatPercentageOptions): string {
  const { inputScale, decimals = null, showSymbol = true, signed = false, fallback = 'NA' } = options;

  if (!isNumber(value)) return fallback;

  const percentageValue = inputScale === 'decimal' ? (value || 0) * 100 : value || 0;
  let formatted: string;

  if (decimals !== null) {
    formatted = percentageValue.toFixed(decimals);
  } else {
    formatted = formatAdaptiveMetric(percentageValue, 'percentage');
  }

  const signPrefix = signed && percentageValue > 0 ? '+' : '';
  const symbol = showSymbol ? '%' : '';
  return `${signPrefix}${formatted}${symbol}`;
}

/** Format a duration for display. */
export function formatDuration(value: number | null | undefined, options: FormatDurationOptions): string {
  const fallback = options.fallback ?? '-';
  const resolved = resolveFallback(value, fallback);
  if (resolved !== null) return resolved;

  const num = value as number;

  if (options.inputUnit === 'hours') {
    return formatDurationHumanHours(num);
  }

  const style = options.style ?? 'human';
  if (style === 'numeric') {
    const decimals = options.decimals ?? 2;
    return `${num.toFixed(decimals)}s`;
  }

  return formatDurationHumanSeconds(num);
}

/** Format a currency value for display (USD only). */
export function formatCurrency(value: number | null | undefined, options?: FormatCurrencyOptions): string {
  const { style = 'raw', currency = DEFAULT_CURRENCY, decimals = null, fallback = '-' } = options ?? {};

  if (value == null || !Number.isFinite(value)) return fallback;

  // Only USD supported — ignore other codes for now.
  void currency;

  if (style === 'localized') {
    const fractionDigits = decimals ?? 2;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: DEFAULT_CURRENCY,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  }

  const showSymbol = options?.currency === '$';
  return formatRawCurrencyValue(value, showSymbol);
}

/** Format a score / rating for display. */
export function formatScore(value: number | null | undefined, options?: FormatScoreOptions): string {
  const { decimals = 1, outOf, fallback = '-' } = options ?? {};

  if (value == null || Number.isNaN(value)) return fallback;

  const formatted = decimals === 0 ? Math.round(value).toString() : value.toFixed(decimals);
  return outOf !== undefined ? `${formatted}/${outOf}` : formatted;
}

/** Compact number with two decimals for metric and datatable charts. */
export function formatChartMetricValue(value: number | null | undefined): string {
  return formatNumber(value ?? 0, FORMAT_PRESETS.chartMetric);
}

/** Compact currency metric for charts (prepends "$"). */
export function formatChartCurrencyMetricValue(value: number | null | undefined): string {
  return formatNumber(value ?? 0, FORMAT_PRESETS.chartMetricCurrency);
}

const SPEND_SUB_CENT_THRESHOLD = 0.01;

/** Delta aligned with compact formatSpend cent display (round each operand to cents first). */
export function computeDisplaySpendDelta(minuend: number, subtrahend: number): number {
  return (Math.round(minuend * 100) - Math.round(subtrahend * 100)) / 100;
}

/** Whether spend is at or below a benchmark using raw amounts (not cent-rounded display delta). */
export function isSpendAtOrBelowBenchmark(value: number, benchmark: number): boolean {
  return value <= benchmark;
}

export type FormatSpendOptions = {
  /** 'compact' (default) → "$1.23M". 'exact' → "$1,234,567.89", no sub-cent clamp. */
  precision?: 'compact' | 'exact';
  fallback?: string;
  /** 'show' (default) keeps "$". 'hide' strips it for icon-prefixed cells. */
  symbol?: 'show' | 'hide';
};

/** Spend for display: always "$", compact from 1K, "< $0.01" below one cent. */
export function formatSpend(value: number | null | undefined, options?: FormatSpendOptions): string {
  const { precision = 'compact', fallback = '-', symbol = 'show' } = options ?? {};

  if (value == null || !Number.isFinite(value)) return fallback;

  let formatted: string;

  if (precision === 'exact') {
    const fractionDigits = Math.abs(value) < 0.01 ? 6 : 2;
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: DEFAULT_CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  } else if (value === 0) {
    formatted = '$0';
  } else {
    const absValue = Math.abs(value);
    if (absValue < SPEND_SUB_CENT_THRESHOLD) {
      formatted = value < 0 ? '> -$0.01' : '< $0.01';
    } else if (absValue >= 1000) {
      formatted = formatNumber(value, FORMAT_PRESETS.chartMetricCurrency);
    } else {
      formatted = formatCurrency(value, { style: 'localized', decimals: 2 });
    }
  }

  return symbol === 'hide' ? formatted.replaceAll('$', '') : formatted;
}

function resolveSpendAxisTickDecimals(stepSize: number): number {
  if (!Number.isFinite(stepSize) || stepSize <= 0) return 2;
  if (stepSize >= 1) return 0;
  return Math.max(2, Math.min(6, Math.ceil(-Math.log10(stepSize))));
}

/** Spend axis tick: always "$", compact from 1K, decimals derived from the tick step. */
export function formatSpendAxisTick(value: number, stepSize: number): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1000) {
    return formatNumber(value, FORMAT_PRESETS.chartMetricCurrency);
  }

  const decimals = resolveSpendAxisTickDecimals(stepSize);
  return `${sign}$${absValue.toFixed(decimals)}`;
}
